/** Ours, not shadcn's: shadcn ships no multi-select, so there is no upstream copy to re-sync with. */
import * as React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

import { cn } from '@ui/lib/utils';
import { Checkbox } from '@ui/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@ui/components/ui/command';
import { Label } from '@ui/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/ui/popover';

export type MultiSelectOption = {
  value: string;
  label: string;
  description?: string;
  /** Short pills at the end of the row, kept out of `label` so the closed trigger stays a plain name. */
  tags?: { text: string; tone?: 'neutral' | 'warning' }[];
};

const TAG_TONE: Record<'neutral' | 'warning', string> = {
  neutral: 'bg-muted text-muted-foreground',
  warning: 'bg-amber-200/70 text-amber-900 dark:bg-amber-200/15 dark:text-amber-200',
};

/** Substring rather than cmdk's fuzzy default, which on a long list matches almost everything. */
export const matchesSubstring = (value: string, search: string, keywords?: string[]): number => {
  const haystack = [value, ...(keywords ?? [])].join(' ').toLowerCase();
  return haystack.includes(search.toLowerCase()) ? 1 : 0;
};

/** Substring over the label and description only, for options whose value is an id nobody reads. */
export const matchesLabel = (_value: string, search: string, keywords?: string[]): number =>
  matchesSubstring('', search, keywords);

export type MultiSelectFieldProps = {
  id?: string;
  label: React.ReactNode;
  options: MultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  /** Shown in the trigger when `value.length === 0` */
  emptyLabel: string;
  /** Placeholder for the search input inside the dropdown. */
  searchPlaceholder?: string;
  /** Copy shown when the search yields no result. */
  emptySearchLabel?: string;
  /** Which edge the panel hangs from, for a field near the wall of a box that clips. */
  align?: 'start' | 'center' | 'end';
  /** Marks the label and the trigger as a choice the form will not go without. */
  required?: boolean;
  /** Aria-label for the clear button (visible when at least one option is selected). */
  clearLabel?: string;
  /** When more than this many options are selected, the trigger shows a count instead of the names. */
  summaryThreshold?: number;
  /** Renders the trigger summary when `value.length > summaryThreshold`. Receives the selected count. */
  renderCountSummary?: (count: number) => string;
  /**
   * When set, a "select all / clear" master row with this label is shown at the top of the dropdown
   * (toggles every option on/off), and the inline clear (X) on the trigger is hidden — a tidier home
   * for the clear affordance than the trigger itself.
   */
  selectAllLabel?: string;
  /**
   * Controlled master row. Left out, it ticks itself whenever every option is ticked and means nothing
   * more than that. Passed in, it is a separate answer the caller keeps — "everything, including what
   * does not exist yet" — which a count of the options can neither hold nor tell apart from it.
   */
  allSelected?: boolean;
  onToggleAll?: (next: boolean) => void;
  /** Single-choice mode: picking an option replaces the selection and closes; rows show a check, not a checkbox. */
  single?: boolean;
  /** Hides the in-dropdown search field (useful for short option lists). Defaults to shown. */
  searchable?: boolean;
  /** How the search scores an option, 0 to hide it. Defaults to cmdk's fuzzy score; see `matchesSubstring`. */
  filter?: (value: string, search: string, keywords?: string[]) => number;
  /** Seeds the search box so a long list arrives narrowed; closing returns the box to this seed. */
  defaultSearch?: string;
  /** Whether the inline clear (X) is offered when something is selected. Defaults to true; set false
   * for a required single choice that must always keep a value. */
  clearable?: boolean;
  /**
   * Controlled open state. Left out, the field opens and closes itself; passed in, a caller can close
   * it — needed where the field can be taken off screen while its list is still showing.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Extra classes for the list itself, for a caller that needs to change how it comes and goes. */
  contentClassName?: string;
  className?: string;
  disabled?: boolean;
};

export function MultiSelectField({
  id: idProp,
  label,
  options,
  value,
  onChange,
  emptyLabel,
  searchPlaceholder = 'Search...',
  emptySearchLabel = 'No matches.',
  clearLabel = 'Clear selection',
  summaryThreshold = 3,
  renderCountSummary,
  selectAllLabel,
  allSelected: allSelectedProp,
  onToggleAll,
  single,
  searchable = true,
  filter,
  defaultSearch,
  clearable = true,
  open: openProp,
  onOpenChange,
  contentClassName,
  className,
  disabled,
  required,
  align = 'start',
}: MultiSelectFieldProps) {
  const genId = React.useId();
  const id = idProp ?? genId;
  const [search, setSearch] = React.useState(defaultSearch ?? '');
  const [openState, setOpenState] = React.useState(false);
  const open = openProp ?? openState;
  const setOpen = (next: boolean): void => {
    setOpenState(next);
    // The field outlives its popup, so a typed filter would still be narrowing the list on the next open.
    if (!next) setSearch(defaultSearch ?? '');
    onOpenChange?.(next);
  };

  const toggle = (optionValue: string) => {
    if (single) {
      // Re-picking the current choice clears it, unless the field is required (`clearable={false}`) —
      // then the dropdown just closes, so there is no way to leave a required choice empty.
      const isDeselecting = value.includes(optionValue);
      if (!isDeselecting) onChange([optionValue]);
      else if (clearable) onChange([]);
      setOpen(false);
      return;
    }
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const allSelected = allSelectedProp ?? (options.length > 0 && value.length === options.length);
  const toggleAll = () =>
    onToggleAll ? onToggleAll(!allSelected) : onChange(allSelected ? [] : options.map(o => o.value));

  // Above `summaryThreshold`, the trigger collapses to a count instead of an ellipsis-truncated
  // list — clearer when many options are selected (the dropdown still shows the full ticked list).
  const summary = (() => {
    if (value.length === 0) return emptyLabel;
    if (value.length > summaryThreshold) {
      return renderCountSummary ? renderCountSummary(value.length) : `${value.length} selected`;
    }
    const labels = value
      .map(v => options.find(o => o.value === v)?.label)
      .filter(Boolean)
      .join(', ');
    // Single only: a lone choice with no option left (an archived product) is no choice, but a filter still filters.
    return single && !labels ? emptyLabel : labels;
  })();

  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          id={id}
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-8 w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-input bg-popover px-3! text-left text-sm shadow-xs outline-none transition-colors',
            'hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
            'disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30',
          )}
        >
          <span className="min-w-0 flex-1 truncate text-foreground">{summary}</span>
          {clearable && value.length > 0 && !disabled && !selectAllLabel ? (
            // Using a span with onMouseDown stops the Popover from toggling and resets the
            // selection inline (HTML disallows nesting a real <button> inside the trigger).
            <span
              role="button"
              tabIndex={0}
              aria-label={clearLabel}
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                onChange([]);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange([]);
                }
              }}
              className="inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <X className="size-4" aria-hidden />
            </span>
          ) : null}
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        </PopoverTrigger>
        <PopoverContent
          className={cn('w-[min(100vw-2rem,var(--anchor-width))] max-w-none p-0', contentClassName)}
          align={align}
          // Fixed, so the list scrolling its first row into view before the popup is placed cannot scroll the page.
          positionMethod="fixed"
        >
          <Command filter={filter} className="rounded-lg border-none bg-transparent shadow-none">
            {searchable && (
              <CommandInput placeholder={searchPlaceholder} className="h-9" value={search} onValueChange={setSearch} />
            )}
            {selectAllLabel && options.length > 0 && (
              // A full-width, bordered header row (not a CommandItem) so it reads as a master toggle
              // rather than another option, and isn't filtered out while searching.
              <button
                type="button"
                onClick={toggleAll}
                disabled={disabled}
                className="flex w-full items-center gap-2 border-b border-border px-4 py-2.5 text-left text-sm font-semibold text-foreground transition-colors hover:bg-muted/50"
              >
                <Checkbox checked={allSelected} aria-hidden tabIndex={-1} />
                <span className="min-w-0 flex-1">{selectAllLabel}</span>
              </button>
            )}
            <CommandList className="max-h-60">
              <CommandEmpty>{emptySearchLabel}</CommandEmpty>
              <CommandGroup className="p-2">
                {options.map(opt => {
                  const cbId = `${id ?? 'multi-select'}-opt-${opt.value}`;
                  return (
                    <CommandItem
                      key={opt.value}
                      value={opt.value}
                      keywords={[opt.label, opt.description].filter((k): k is string => Boolean(k))}
                      onSelect={() => toggle(opt.value)}
                      className={cn(
                        'mb-1 flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-sm aria-selected:bg-muted/60',
                      )}
                      disabled={disabled}
                    >
                      {single ? (
                        <Check
                          className={cn(
                            'size-4 shrink-0 text-primary',
                            value.includes(opt.value) ? 'opacity-100' : 'opacity-0',
                          )}
                          aria-hidden
                        />
                      ) : (
                        <Checkbox
                          id={cbId}
                          checked={value.includes(opt.value)}
                          disabled={disabled}
                          aria-hidden
                          tabIndex={-1}
                        />
                      )}
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate">{opt.label}</span>
                        {opt.description ? (
                          <span className="truncate text-xs text-muted-foreground">{opt.description}</span>
                        ) : null}
                      </span>
                      {/* Held out of the truncation: the pill is the reason to read the row twice. */}
                      {(opt.tags ?? []).map(pill => (
                        <span
                          key={pill.text}
                          className={cn(
                            'shrink-0 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-medium',
                            TAG_TONE[pill.tone ?? 'neutral'],
                          )}
                        >
                          {pill.text}
                        </span>
                      ))}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
